import {
	buildPageStaggerIndexMap,
	computePageStaggerTimings,
	shouldApplyStaggerPageElementTransition,
} from "./escapeRoomPageStaggerUtils";

/** Transiciones escalonadas solo por efecto visual (sin desplazamiento). */
export const STAGGER_EFFECT_TRANSITION_IDS = new Set([
	"materialize",
	"build",
	"static",
	"thermal",
	"softGlitch",
	"mist",
	"shadow",
	"omen",
	"clinical",
]);

export const STAGGER_PANEL_TRANSITION_IDS = new Set([
	"comic",
	"scatter",
	"reveal",
	...STAGGER_EFFECT_TRANSITION_IDS,
]);

export function isStaggerEffectTransition(id) {
	return STAGGER_EFFECT_TRANSITION_IDS.has(String(id || ""));
}

export { buildPageStaggerIndexMap as buildStaggerEffectIndexMap };

export function shouldApplyStaggerEffectPageElementTransition(type, ctx) {
	return shouldApplyStaggerPageElementTransition(type, ctx);
}

function hashFromId(id) {
	let hash = 0;
	const s = String(id ?? "");
	for (let i = 0; i < s.length; i += 1) hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
	return Math.abs(hash);
}

/** Tres direcciones de “desmontaje” (explosionado) según índice de stagger. */
export function buildAssembleOffsetVars(staggerIndex) {
	const i = Math.max(0, Number(staggerIndex) || 0);
	const mod = i % 3;
	if (mod === 0) return { "--er-build-dx": "-18px", "--er-build-dy": "-6px" };
	if (mod === 1) return { "--er-build-dx": "20px", "--er-build-dy": "5px" };
	return { "--er-build-dx": "0px", "--er-build-dy": "18px" };
}

function isBuildInteractiveElement(element) {
	const t = String(element?.type || "");
	return t === "button" || t === "quiz" || t === "hint";
}

function cssVarsForEffect(type, elementId, staggerIndex) {
	const h = hashFromId(elementId);
	if (type === "materialize") {
		return {
			"--er-mat-blur": `${11 + (h % 6)}px`,
			"--er-mat-glow": (0.45 + ((h >> 3) % 35) / 100).toFixed(2),
		};
	}
	if (type === "build") {
		return buildAssembleOffsetVars(staggerIndex);
	}
	if (type === "static") {
		return { "--er-static-seed": String(h % 97) };
	}
	if (type === "thermal") {
		return { "--er-neon-hue": `${(h % 240) - 120}deg` };
	}
	if (type === "softGlitch") {
		const sign = h % 2 === 0 ? 1 : -1;
		return {
			"--er-glitch-x": `${sign * (7 + (h % 9))}px`,
			"--er-glitch-y": `${sign * -1 * (3 + (h % 6))}px`,
			"--er-glitch-skew": `${sign * (4 + (h % 5))}deg`,
		};
	}
	if (type === "mist") {
		const sign = h % 2 === 0 ? 1 : -1;
		return {
			"--er-mist-drift": `${sign * (4 + (h % 8))}px`,
			"--er-mist-blur": `${h % 5}px`,
		};
	}
	if (type === "shadow") {
		const sign = h % 2 === 0 ? 1 : -1;
		return { "--er-shadow-x": `${sign * (5 + (h % 7))}px` };
	}
	if (type === "omen") {
		const sign = h % 2 === 0 ? 1 : -1;
		return { "--er-omen-tilt": `${sign * (2 + (h % 4))}deg` };
	}
	if (type === "clinical") {
		const sign = h % 2 === 0 ? 1 : -1;
		return {
			"--er-clinical-rise": `${6 + (h % 8)}px`,
			"--er-clinical-glow": (0.35 + ((h >> 2) % 40) / 100).toFixed(2),
			"--er-clinical-skew": `${sign * (0.4 + (h % 3) * 0.35)}deg`,
		};
	}
	return {};
}

/** Ensamblaje: pieza ~480ms + baseDelay 80ms + step ~90ms. */
const EFFECT_TIMING = {
	materialize: { panelRatio: 0.54, bgPanelRatio: 0.42, bgFactor: 0.65, easing: "ease-out" },
	build: {
		panelRatio: 0.5,
		bgPanelRatio: 0.36,
		bgFactor: 0.45,
		easing: "cubic-bezier(0.16, 1, 0.3, 1)",
		baseDelaySec: 0.08,
		fixedPanelSec: 0.48,
		fixedStepSec: 0.09,
	},
	static: { panelRatio: 0.5, bgPanelRatio: 0.4, bgFactor: 0.6, easing: "steps(6, end)" },
	thermal: { panelRatio: 0.4, bgPanelRatio: 0.28, bgFactor: 0.45, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
	softGlitch: { panelRatio: 0.46, bgPanelRatio: 0.3, bgFactor: 0.35, easing: "steps(10, end)" },
	mist: { panelRatio: 0.5, bgPanelRatio: 0.34, bgFactor: 0.55, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
	shadow: { panelRatio: 0.52, bgPanelRatio: 0.36, bgFactor: 0.58, easing: "cubic-bezier(0.45, 0, 0.2, 1)" },
	omen: { panelRatio: 0.48, bgPanelRatio: 0.32, bgFactor: 0.42, easing: "steps(8, end)" },
	clinical: { panelRatio: 0.48, bgPanelRatio: 0.34, bgFactor: 0.5, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
};

const TRANSFORM_EFFECTS = new Set(["softGlitch", "thermal", "mist", "shadow", "omen", "build", "clinical"]);

export function getStaggerEffectPageTransitionElementStyle(type, staggerIndex, totalCount, durationMs, element) {
	const effectType = String(type || "");
	if (!STAGGER_EFFECT_TRANSITION_IDS.has(effectType)) return {};
	const idx = Math.max(0, staggerIndex || 0);
	const timing = EFFECT_TIMING[effectType] || EFFECT_TIMING.materialize;

	let panelSec;
	let delay;
	if (effectType === "build" && timing.fixedPanelSec != null) {
		panelSec = timing.fixedPanelSec;
		const step = timing.fixedStepSec ?? 0.09;
		const base = timing.baseDelaySec ?? 0.08;
		delay = base + idx * step;
	} else {
		const computed = computePageStaggerTimings(durationMs, totalCount, { panelRatio: timing.panelRatio });
		panelSec = computed.panelSec;
		delay = computed.delaySec(idx);
	}

	return {
		opacity: 0,
		...cssVarsForEffect(effectType, element?.elementId, idx),
		animation: `er-page-${effectType}-panel ${panelSec}s ${timing.easing} ${delay}s both`,
		willChange: TRANSFORM_EFFECTS.has(effectType) ? "opacity, transform, filter" : "opacity, filter",
		// drop-shadow del ensamblaje agranda el hit-box: textos/imágenes no deben tapar botones/quizzes
		...(effectType === "build" && !isBuildInteractiveElement(element) ? { pointerEvents: "none" } : {}),
	};
}

export function getStaggerEffectPageBackgroundStyle(type, durationMs) {
	const effectType = String(type || "");
	if (!STAGGER_EFFECT_TRANSITION_IDS.has(effectType)) return {};
	const timing = EFFECT_TIMING[effectType] || EFFECT_TIMING.materialize;
	const { totalSec } = computePageStaggerTimings(durationMs, 1, { panelRatio: timing.bgPanelRatio });
	const sec = Math.max(0.22, totalSec * timing.bgFactor);
	const bgFilterByType = {
		build: "brightness(0.88) contrast(1.04)",
		static: "contrast(2.2) brightness(1.5) saturate(0)",
		thermal: "brightness(0.45) saturate(1.4) contrast(1.2)",
		softGlitch: "contrast(1.8) saturate(0.7) brightness(1.2)",
		mist: "blur(14px) saturate(0.22) brightness(0.65) contrast(0.9)",
		shadow: "brightness(0.22) contrast(1.5) saturate(0.15)",
		omen: "brightness(0.4) saturate(0.35) hue-rotate(280deg) contrast(1.3)",
		clinical: "brightness(0.45) saturate(1.15) hue-rotate(-18deg) contrast(1.12)",
	};
	return {
		opacity: 0,
		filter: bgFilterByType[effectType] || "blur(4px) saturate(0.35) brightness(0.65)",
		animation: `er-page-${effectType}-bg ${sec}s ease-out both`,
		willChange: "opacity, filter",
	};
}
